import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from './useAuth'
import { isExternal } from '../lib/roleHelpers'
import { showToast } from '../components/ui/index'

export function useHubs() {
  const { profile } = useAuth()
  const [hubs, setHubs]       = useState([])
  const [loading, setLoading] = useState(true)

  const fetchHubs = useCallback(async () => {
    if (!profile?.id) return
    const { data, error } = await supabase
      .from('hubs')
      .select('*, hub_members!inner(profile_id, role)')
      .eq('hub_members.profile_id', profile.id)
      .order('created_at', { ascending: false })
    if (error) { showToast('Failed to load hubs', 'error'); return }

    // Enrich with member count and user's role
    const enriched = await Promise.all((data || []).map(async hub => {
      const { count } = await supabase
        .from('hub_members')
        .select('*', { count: 'exact', head: true })
        .eq('hub_id', hub.id)
      const myRole = hub.hub_members?.find(m => m.profile_id === profile.id)?.role || 'member'
      return { ...hub, member_count: count || 0, my_role: myRole, hub_members: undefined }
    }))

    setHubs(enriched)
    setLoading(false)
  }, [profile?.id])

  useEffect(() => {
    fetchHubs()
  }, [fetchHubs])

  const createHub = useCallback(async ({ name, description, icon, color }) => {
    if (!profile?.id || !name.trim()) return null
    if (isExternal(profile)) {
      showToast('Agents and clients cannot create hubs', 'error')
      return null
    }
    // Atomic hubs + hub_members(owner) insert via SECURITY DEFINER RPC
    // (migration 091). Replaces a previous two-step client flow that left
    // 15 orphan hubs in prod when the second insert silently failed.
    const { data: hub, error } = await supabase
      .rpc('create_hub_with_owner', {
        p_name: name.trim(),
        p_description: description?.trim() || null,
        p_icon: icon ?? null,
        p_color: color ?? null,
      })
    if (error || !hub) {
      console.error('createHub failed:', error)
      showToast(error?.message || 'Failed to create hub', 'error')
      return null
    }

    await fetchHubs()
    showToast('Hub created')
    return hub
  }, [profile?.id, fetchHubs])

  const updateHub = useCallback(async (hubId, updates) => {
    const { error } = await supabase.from('hubs').update(updates).eq('id', hubId)
    if (error) { showToast('Failed to update hub', 'error'); return false }
    await fetchHubs()
    return true
  }, [fetchHubs])

  const deleteHub = useCallback(async (hubId) => {
    const { error } = await supabase.from('hubs').delete().eq('id', hubId)
    if (error) {
      console.error('deleteHub failed:', error)
      // RLS rejection shows up here as "permission denied for table hubs".
      // The friendlier message preserves prior UX; the console line above
      // gives ops the actionable detail.
      showToast(error.message?.includes('permission')
        ? 'Only the hub owner or an Admin can delete this hub'
        : 'Failed to delete hub', 'error')
      return false
    }
    await fetchHubs()
    showToast('Hub deleted')
    return true
  }, [fetchHubs])

  return { hubs, loading, createHub, updateHub, deleteHub, refetch: fetchHubs }
}
